import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import { toast } from 'sonner'
import {
  ArrowLeft,
  Loader2,
  Shield,
  Mail,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  TrendingUp,
} from 'lucide-react'

interface BlacklistHit {
  rbl: string
  ip: string
  listed: boolean
  at: string
}

interface AbuseFlag {
  type: string
  severity: string
  status: string
  created_at: string
}

interface DomainDeliverabilityDetail {
  domain_id: string
  domain_name: string
  reputation_score: number
  dns_health: Record<string, string>
  sent: number
  delivered: number
  bounced: number
  spam: number
  delivery_rate_7d: number
  bounce_rate_7d: number
  complaint_rate_7d: number
  emails_sent_7d: number
  emails_delivered_7d: number
  delivery_rates: Record<string, number>
  warmup_progress: number
  blacklist_hits: BlacklistHit[]
  abuse_flags: AbuseFlag[]
  last_calculated_at: string | null
}

// CHECKPOINT: PRD-08 FR-8.5.1 — Per-domain reputation detail view
export function DeliverabilityDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [data, setData] = useState<DomainDeliverabilityDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const fetchDetail = async () => {
    if (!id) return
    try {
      const res = await api.get(`/deliverability/domains/${id}`)
      setData(res.data)
    } catch {
      toast.error('Failed to load domain deliverability details')
      navigate('/deliverability')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDetail()
  }, [id])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-violet-400" />
      </div>
    )
  }

  if (!data) return null

  const deliveryRate = Math.round((data.delivery_rate_7d || 0) * 100)
  const bounceRate = Math.round((data.bounce_rate_7d || 0) * 100)
  const complaintRate = Math.round((data.complaint_rate_7d || 0) * 100)

  const reputationStatus =
    data.reputation_score >= 80 ? 'active' : data.reputation_score >= 60 ? 'pending' : 'suspended'

  const dnsEntries = data.dns_health ? Object.entries(data.dns_health) : []
  const allDnsVerified = dnsEntries.every(([, status]) => status === 'verified')

  const providerLabels: Record<string, string> = {
    gmail: 'Gmail',
    outlook: 'Outlook / Hotmail',
    yahoo: 'Yahoo',
    icloud: 'iCloud',
    other: 'Other',
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/deliverability')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold tracking-tight">{data.domain_name}</h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <StatusBadge status={reputationStatus} />
            <span className="text-sm text-muted-foreground">
              Reputation {data.reputation_score}/100 · Last updated{' '}
              {data.last_calculated_at
                ? new Date(data.last_calculated_at).toLocaleString()
                : 'never'}
            </span>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={fetchDetail}>
          <Loader2 className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={<Mail className="h-4 w-4 text-blue-400" />}
          label="Sent (7d)"
          value={data.sent}
        />
        <StatCard
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-400" />}
          label="Delivered"
          value={data.delivered}
          subValue={`${deliveryRate}% rate`}
          positive
        />
        <StatCard
          icon={<XCircle className="h-4 w-4 text-red-400" />}
          label="Bounced"
          value={data.bounced}
          subValue={`${bounceRate}% rate`}
          negative={bounceRate > 5}
        />
        <StatCard
          icon={<AlertTriangle className="h-4 w-4 text-amber-400" />}
          label="Complaints"
          value={data.spam}
          subValue={`${complaintRate}% rate`}
          negative={complaintRate > 0}
        />
      </div>

      {/* DNS + Warmup */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Shield className="h-4 w-4 text-violet-400" />
              DNS Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {dnsEntries.length === 0 ? (
              <p className="text-sm text-muted-foreground">No DNS health data available.</p>
            ) : (
              <>
                {dnsEntries.map(([key, status]) => (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="capitalize text-muted-foreground">{key}</span>
                    <span className={status === 'verified' ? 'text-emerald-400' : 'text-red-400'}>
                      {status === 'verified' ? 'Verified' : status}
                    </span>
                  </div>
                ))}
                <div className="pt-2 border-t">
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>Overall</span>
                    <span className={allDnsVerified ? 'text-emerald-400' : 'text-amber-400'}>
                      {allDnsVerified ? 'All records verified' : 'Some records pending'}
                    </span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-violet-400" />
              Warmup Progress
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{data.warmup_progress ?? 0}%</span>
            </div>
            <Progress value={data.warmup_progress ?? 0} />
            <p className="text-xs text-muted-foreground">
              New domains ramp up sending volume over 29 days. Full volume is available once
              warmup reaches 100%.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Provider breakdown */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Mail className="h-4 w-4 text-violet-400" />
            Delivery Rate by Provider (30d)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {Object.keys(data.delivery_rates || {}).length === 0 ? (
            <EmptyState
              title="No provider data"
              description="Provider delivery rates will appear once emails are delivered."
              icon={<Mail className="h-5 w-5" />}
            />
          ) : (
            <div className="space-y-3">
              {Object.entries(data.delivery_rates).map(([provider, rate]) => {
                const pct = Math.round((rate || 0) * 100)
                return (
                  <div key={provider} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {providerLabels[provider] || provider}
                      </span>
                      <span className="font-medium">{pct}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-primary/20 overflow-hidden">
                      <div
                        className="h-full bg-primary transition-all"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Blacklist hits */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-violet-400" />
            Recent Blacklist Hits
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.blacklist_hits || data.blacklist_hits.length === 0 ? (
            <EmptyState
              title="No blacklist hits"
              description="Your domain's sending IP is clean across monitored RBLs."
              icon={<CheckCircle2 className="h-5 w-5" />}
            />
          ) : (
            <div className="space-y-2">
              {data.blacklist_hits.map((hit, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium">{hit.rbl}</p>
                    <p className="text-xs text-muted-foreground">IP: {hit.ip}</p>
                  </div>
                  <span className="text-xs text-red-400">
                    {hit.at ? new Date(hit.at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Abuse flags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Shield className="h-4 w-4 text-violet-400" />
            Recent Abuse Flags
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!data.abuse_flags || data.abuse_flags.length === 0 ? (
            <EmptyState
              title="No abuse flags"
              description="No recent abuse flags for this domain."
              icon={<CheckCircle2 className="h-5 w-5" />}
            />
          ) : (
            <div className="space-y-2">
              {data.abuse_flags.map((flag, idx) => (
                <div
                  key={idx}
                  className="flex items-center justify-between rounded-lg border px-3 py-2"
                >
                  <div>
                    <p className="text-sm font-medium capitalize">{flag.type.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-muted-foreground">
                      Severity: {flag.severity} · Status: {flag.status}
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {flag.created_at ? new Date(flag.created_at).toLocaleString() : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function StatCard({
  icon,
  label,
  value,
  subValue,
  positive,
  negative,
}: {
  icon: React.ReactNode
  label: string
  value: number
  subValue?: string
  positive?: boolean
  negative?: boolean
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-lg bg-accent flex items-center justify-center">{icon}</div>
          <span className="text-sm font-medium text-muted-foreground">{label}</span>
        </div>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-bold">{value.toLocaleString()}</span>
          {subValue && (
            <span
              className={`text-xs font-medium ${
                positive ? 'text-emerald-400' : negative ? 'text-red-400' : 'text-muted-foreground'
              }`}
            >
              {subValue}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
